import {
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ContentChange, QuillEditorComponent } from 'ngx-quill';
import {
  ReactiveFormsModule,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import 'quill-paste-smart';
import { Delta } from 'quill/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Subscription } from 'rxjs';
@Component({
  selector: 'app-rte-form-field',
  standalone: true,
  imports: [
    QuillEditorComponent,
    ReactiveFormsModule,
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
  ],
  templateUrl: './rte-form-field.component.html',
  styleUrl: './rte-form-field.component.css',
})
export class RteFormFieldComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  @Input() label!: string;
  @Input() maxLength: number = -1;
  @Input() placeholder: string = '';

  charCount: number = 0;

  value: string = '';

  onChange = (value: unknown) => {};
  onTouched = () => {};

  touched: boolean = false;
  disabled: boolean = false;
  focused: boolean = false;
  invalid: boolean = false;

  editorCreatedSubscription?: Subscription;

  @ViewChild(QuillEditorComponent, { static: true }) editor:
    | QuillEditorComponent
    | undefined;

  editorOptions = {
    toolbar: [['bold', 'italic', 'underline', 'strike'], ['link']],
    clipboard: {
      matchVisual: false,
      allowed: {
        tags: ['a', 'b', 'i', 'strong', 'u', 's', 'br', 'p', 'div', 'span'],
        attributes: ['href'],
      },
    },
  };

  disabledEditorOptions = {
    toolbar: false,
  };

  constructor(public control: NgControl) {
    this.control.valueAccessor = this;
  }

  ngOnInit() {
    this.editorCreatedSubscription = this.editor?.onEditorCreated.subscribe(
      () => {
        this.charCount = (this.editor?.quillEditor.getLength() || 1) - 1;
      }
    );
  }

  ngOnDestroy(): void {
    this.editorCreatedSubscription?.unsubscribe();
  }

  writeValue(obj: any): void {
    this.value = obj;
    this.editor?.writeValue(this.value);
    if (this.editor?.quillEditor) {
      this.charCount = this.editor?.quillEditor.getLength() - 1;
    }
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  markAsTouched() {
    if (!this.touched) {
      this.touched = true;
      this.onTouched();
    }
  }

  onContentChanged(event: ContentChange): void {
    this.charCount = (event.editor.getLength() || 1) - 1;
    // Model writes and Quill initialization must not report a view edit to Angular.
    if (event.source !== 'user') return;

    this.markAsTouched();
    let content = event.content;
    if (this.maxLength > 0 && this.charCount > this.maxLength) {
      event.editor.deleteText(this.maxLength, this.charCount - this.maxLength, 'api');
      content = event.editor.getContents();
      this.charCount = event.editor.getLength() - 1;
    }
    this.value = this.convertDeltaToString(content);

    this.onChange(this.value);
  }
  handleQuillFocus() {
    this.focused = true;
  }
  handleQuillBlur() {
    this.focused = false;
  }

  convertDeltaToString(delta: Delta): string {
    const parts = delta.ops
      .map((op) => {
        let text = String(op.insert);

        if (op.attributes) {
          if (op.attributes['strike']) {
            text = `<s>${text}</s>`;
          }
          if (op.attributes['underline']) {
            text = `<u>${text}</u>`;
          }
          if (op.attributes['italic']) {
            text = `<i>${text}</i>`;
          }
          if (op.attributes['bold']) {
            text = `<b>${text}</b>`;
          }
          if (op.attributes['link']) {
            text = `<a href="${op.attributes['link']}" target="_blank" style="color: #0e2645; text-decoration:underline;">${text}</a>`;
          }
        }

        return text;
      })
      .join('')
      .trim()
      .replace(/(\r\n|\n|\r)/g, '<br />');
    return parts;
  }

  onPasteButtonClick() {
    navigator.clipboard
      .readText()
      .then((text) => {
        this.editor?.quillEditor.insertText(
          this.editor.quillEditor.getLength() - 1,
          text,
          'user'
        );
      })
      .catch((err) => {
        console.error('Failed to read clipboard contents: ', err);
      });
  }
}
